import { useCallback, useEffect, useRef, useState } from 'react'
import CustomizedModal from '../modals/CustomizedModal'
import SuccessButton from '../ui/SuccessButton'
import { useMediaQuery } from '@mui/material'
import classes from './VideoPlayer.module.scss'

interface Props {
  link?: string
  linkText?: string
  videoUrl: string
}
const VideoPlayer = ({ link, linkText, videoUrl }: Props) => {
  const [visible, setVisible] = useState(false)
  const youtubePlayerInstance = useRef<YT.Player>(null)

  const smallScreen = useMediaQuery('(max-width: 959.98px)')

  const destroyPlayer = useCallback(() => {
    if (youtubePlayerInstance.current) {
      try {
        youtubePlayerInstance.current.destroy()
      } catch {
        // player already destroyed or invalid
      }
      youtubePlayerInstance.current = null
    }
  }, [])

  const createPlayer = useCallback(
    (videoId: string) => {
      destroyPlayer()
      youtubePlayerInstance.current = new YT.Player('hytPlayer', {
        videoId,
        width: '100%',
        height: '100%',
        playerVars: { rel: 0 },
        events: {
          onReady: (event) => {
            event.target.playVideo()
          }
        }
      })
    },
    [destroyPlayer]
  )

  const loadYouTubeAPI = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      const w = window as any
      if (w.YT && w.YT.Player) {
        resolve()
        return
      }
      if (
        !document.querySelector(
          'script[src="https://www.youtube.com/iframe_api"]'
        )
      ) {
        const tag = document.createElement('script')
        tag.src = 'https://www.youtube.com/iframe_api'
        const firstScriptTag = document.getElementsByTagName('script')[0]
        firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag)
      }
      w['onYouTubeIframeAPIReady'] = () => resolve()
    })
  }, [])

  const waitForElement = useCallback((id: string): Promise<void> => {
    return new Promise((resolve) => {
      if (document.getElementById(id)) {
        resolve()
        return
      }
      const observer = new MutationObserver(() => {
        if (document.getElementById(id)) {
          observer.disconnect()
          resolve()
        }
      })
      observer.observe(document.body, { childList: true, subtree: true })
    })
  }, [])

  const startVideo = useCallback(
    async (videoId: string) => {
      await Promise.all([loadYouTubeAPI(), waitForElement('hytPlayer')])
      createPlayer(videoId)
    },
    [loadYouTubeAPI, waitForElement, createPlayer]
  )

  useEffect(() => {
    if (videoUrl) {
      setVisible(true)
      startVideo(videoUrl)
    } else {
      destroyPlayer()
      setVisible(false)
    }
    return () => destroyPlayer()
  }, [videoUrl, startVideo, destroyPlayer])

  return (
    <CustomizedModal
      classname={''}
      loadMask={false}
      footerCls="!flex !align-center !justify-center "
      contentCls="!p-0"
      hideFooter={!link}
      closeBtnColor="text-white"
      footer={
        link
          ? [
              <SuccessButton
                fullWidth={false}
                key="link"
                cls="!text-[18px] max-[768px]:leading-[.65rem]"
                asLink={link}>
                {/* View Job Post */}
                {linkText}
              </SuccessButton>
            ]
          : null
      }
      PaperProps={
        smallScreen
          ? {
              style: {
                width: '95%',
                height: 'auto',
                maxWidth: '780px',
                maxHeight: '90vh',
                backgroundColor: '#000'
              }
            }
          : {
              style: {
                width: '90%',
                height: 'auto',
                maxWidth: '780px',
                maxHeight: '90vh',
                backgroundColor: '#000'
              }
            }
      }
      open={visible}
      onCancel={() => {
        destroyPlayer()
        setVisible(false)
      }}>
      <div className={`${classes.hytPlayerWrap}`}>
        <div id="hytPlayer"></div>
      </div>
    </CustomizedModal>
  )
}

export default VideoPlayer
